import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState, DemoBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { ClipboardList, Plus, Settings2 } from "lucide-react";
import WorkflowSheet from "../../components/WorkflowSheet";

const CATS = ["CCTV & Security", "Waterproofing", "Painting", "Electrical", "Plumbing", "Pumps", "Solar", "Fire Safety", "STP", "Lift", "Pest Control", "Housekeeping", "Civil Work", "Other"];
const FLOW = ["Draft", "Submitted", "Under Review", "Inspection Required", "RFQ", "Quotation", "Approved", "Rejected", "Completed", "Cancelled"];

export default function Requirements() {
  const [reqs, setReqs] = useState(null);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [form, setForm] = useState({ category: "", urgency: "Normal" });
  const load = () => api.get("/requirements").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.category) { toast.error("Title and category required"); return; }
    await api.post("/requirements", form); toast.success("Requirement submitted"); setOpen(false); setForm({ category: "", urgency: "Normal" }); load();
  };
  const setStatus = async (id, status) => { await api.put(`/requirements/${id}/status`, { status }); toast.success("Status updated"); load(); };

  if (!reqs) return <Loading />;
  return (
    <div>
      <PageHeader title="Requirements" subtitle="Raise service requirements — we coordinate inspection, RFQ and quotations.">
        <Button onClick={() => setOpen(true)} className="rounded-full gap-2" data-testid="req-add"><Plus className="w-4 h-4" /> Create Requirement</Button>
      </PageHeader>
      {reqs.length === 0 ? <EmptyState icon={ClipboardList} title="No requirements yet" subtitle="Create a requirement for any service your society needs." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {reqs.map((r) => (
            <div key={r.requirement_id} className="bg-card border rounded-xl p-5" data-testid={`req-card-${r.requirement_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs text-muted-foreground font-mono">{r.ref}</div>
                  <h3 className="font-display font-bold">{r.title} {r.is_demo && <DemoBadge />}</h3>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.category} · {r.urgency}</div>
                </div>
                <span className="text-xs rounded-full bg-primary/10 text-primary px-2 py-1 whitespace-nowrap">{r.status}</span>
              </div>
              {r.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{r.description}</p>}
              <div className="mt-3 flex gap-2">
                <Select value={r.status} onValueChange={(v) => setStatus(r.requirement_id, v)}>
                  <SelectTrigger className="h-9 text-sm flex-1" data-testid={`req-status-${r.requirement_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" className="rounded-full gap-1.5 h-9" onClick={() => setManage(r)} data-testid={`req-manage-${r.requirement_id}`}><Settings2 className="w-4 h-4" /> Manage</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="req-dialog">
          <DialogHeader><DialogTitle className="font-display">Create Requirement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1.5 block text-sm">Title *</Label><Input data-testid="req-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-sm">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="req-category"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="mb-1.5 block text-sm">Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Low", "Normal", "High", "Urgent"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="mb-1.5 block text-sm">Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-sm">Location</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label className="mb-1.5 block text-sm">Budget (if known)</Label><Input value={form.budget || ""} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="req-save">Submit Requirement</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <WorkflowSheet requirement={manage} open={!!manage} onClose={() => setManage(null)} onChanged={load} />
    </div>
  );
}
